import { Timestamp } from '@lemon/core/model/common'

/** Lähdejärjestelmä, josta tiedosto on peräisin. Laajennetaan kun uusia formaatteja tuetaan. */
export type AsiakasImportLahdejarjestelma = 'netvisor' | 'procountor'

/** Virhekoodit asiakas-import-operaatioista. Frontend tulkkaa nämä käyttäjälle näytettäviksi teksteiksi. */
export enum AsiakasImportVirhekoodi {
  METADATA_EI_LOYDY = 'metadata-ei-loydy',
  TIEDOSTOA_EI_LOYDY = 'tiedostoa-ei-loydy',
  OTSIKKORIVI_PUUTTUU = 'otsikkorivi-puuttuu',
  SARAKKEET_PUUTTUVAT = 'sarakkeet-puuttuvat',
  EI_ASIAKKAITA = 'ei-asiakkaita',
  PARSINTAVIRHE = 'parsintavirhe',
  LAHDEJARJESTELMA_EPAVARMA = 'lahdejarjestelma-epavarma',
  TUNTEMATON_VIRHE = 'tuntematon-virhe',
}

/** Sisäinen tietomalli: yksi lähdetiedostosta parsittu laskunasiakas-rivi (ennen tallennusta). */
export interface AsiakasImportRivi {
  /** Alkuperäinen rivinumero lähdetiedostossa (1-pohjainen, otsikkorivi ei lasketa) */
  rivinumero: number
  /** Asiakaskoodi lähdejärjestelmässä. Tyhjä jos lähde ei tarjoa koodia. */
  ulkoinenTunniste: string
  nimi: string
  ytunnus: string
  /** Yritysasiakas ('y') tai henkilöasiakas ('h'). */
  asiakastyyppi: 'y' | 'h'
  katuosoite: string
  postinro: string
  postitmp: string
  /** ISO-3 -koodi (esim. 'FIN', 'SWE'). Tuntemattomilla maannimillä raakaa tekstiä uppercasena. */
  maa: string
  /** Verkkolaskuosoite (sahkoinenOsoite) */
  sahkoinenOsoite: string
  /** Operaattorin välittäjätunnus (sahkoinenValittaja). */
  sahkoinenValittaja: string
  /** Sähköpostilaskuosoite — käytetään laskunVastaanottajina jos täytetty. */
  sahkoinenSposti: string
  /** Tuettu kieli — 'fi' tai 'en'. */
  laskunKieli: 'fi' | 'en'
  /** True jos lähdejärjestelmässä on merkitty poistetuksi/passiiviseksi. */
  poistettu: boolean
}

/** Pyyntö tiedoston parsimiseksi. Tiedosto on jo arkistossa (uploadattu hallintokansiossa). */
export interface AsiakasImportParsePyynto {
  asiakasAvain: string
  /** Arkistotiedoston metadata-avain (ArkistoFile.avain) */
  arkistoTiedostonAvain: string
  /** Jos puuttuu, backend tunnistaa lähdejärjestelmän tiedoston sisällöstä. */
  lahdejarjestelma?: AsiakasImportLahdejarjestelma
}

/** Vastaus parsinnasta. Sisältää esikatselurivit, ei tallenna mitään. */
export interface AsiakasImportParseVastaus {
  onnistui: boolean
  virhekoodi?: AsiakasImportVirhekoodi
  virheviesti?: string
  rivit?: AsiakasImportRivi[]
  /** Parsinnassa käytetty lähdejärjestelmä (pyynnössä annettu tai tiedostosta tunnistettu) */
  lahdejarjestelma?: AsiakasImportLahdejarjestelma
}

/** Pyyntö esikatseltujen asiakkaiden tallentamiseksi asiakkaan laskutusasiakasrekisteriin. */
export interface AsiakasImportTallennaPyynto {
  asiakasAvain: string
  rivit: AsiakasImportRivi[]
  arkistoTiedostonAvain: string
  lahdejarjestelma: AsiakasImportLahdejarjestelma
}

/** Vastaus tallennuksesta. */
export interface AsiakasImportTallennaVastaus {
  onnistui: boolean
  virhekoodi?: AsiakasImportVirhekoodi
  tallennetut?: number
  ohitetutDuplikaatit?: number
}

/**
 * Asiakkaan tallennettu asiakas-import-dokumentti — viimeisimmän tuonnin metadata.
 * Firestore-polku: /asiakkaat/{asiakasAvain}/muut/asiakas-import
 */
export interface AsiakasImportDocument {
  tuotu: Timestamp
  lahdejarjestelma: AsiakasImportLahdejarjestelma
  tuoja: string
  arkistoTiedostonAvain: string
  tiedostonNimi: string
  tallennetutAsiakkaat: number
  ohitetutDuplikaatit?: number
}

/**
 * Yksittäisen tuonnin historiamerkintä — yksi rivi per käyttäjän käynnistämä tallennus.
 * Firestore-polku: /asiakkaat/{asiakasAvain}/muut/asiakas-import/asiakas-import-historia/{historiaAvain}
 */
export interface AsiakasImportHistoriaDocument extends AsiakasImportDocument {
  avain: string
}
